import type { ReactNode } from 'react';
import type { ModalProps } from '../overlays/Modal/Modal.js';
import type { DialogProps } from '../overlays/Dialog/Dialog.js';
import { createStackStore } from '../_internal/stack-store.js';

/** Props accepted by `openModal()` - everything `<Modal>` takes minus the controlled `opened`. */
export type OpenModalPayload =
  & Omit<ModalProps, 'opened' | 'onClose' | 'children'>
  & {
    children?: ReactNode;
    /** Called when the modal finishes closing (after animation). */
    onClose?: () => void;
  };

/** Props accepted by `openConfirmModal()`. */
export type OpenConfirmModalPayload =
  & Omit<DialogProps, 'opened' | 'onClose' | 'children' | 'onConfirm' | 'onCancel'>
  & {
    children?: ReactNode;
    onConfirm?: () => void;
    onCancel?: () => void;
    labels?: { confirm?: string; cancel?: string };
    confirmProps?: DialogProps['confirmProps'];
    cancelProps?: DialogProps['cancelProps'];
    /** Close after `onConfirm` returns. Default true. */
    closeOnConfirm?: boolean;
    /** Close after `onCancel` returns. Default true. */
    closeOnCancel?: boolean;
  };

/** Props accepted by `openContextModal()`. */
export type OpenContextModalPayload<TName extends string = string, TInner = unknown> =
  & Omit<ModalProps, 'opened' | 'onClose' | 'children'>
  & {
    modal: TName;
    innerProps: TInner;
    onClose?: () => void;
  };

export type ModalItem =
  | { kind: 'modal'; payload: OpenModalPayload }
  | { kind: 'confirm'; payload: OpenConfirmModalPayload }
  | { kind: 'context'; payload: OpenContextModalPayload };

export const modalsStore = createStackStore<ModalItem>('rr-modal');
